import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import MapMarker from "mdi-react/MapMarkerIcon";
import { AvField, AvForm } from "availity-reactstrap-validation";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

import { ReactComponent as EditSvg } from "../../../../assets/icons/edit.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/info.svg";
import { ReactComponent as GoogleIcon } from "../../../../assets/icons/google.svg";
import { ReactComponent as MapMarkerSvg } from "../../../../assets/icons/map-marker.svg";
import {
  SPACE,
  messages,
  EMPTY_STRING,
  discountStatus,
  CURRENCY_SYMBOL,
  eventBookingStatus,
} from "../../../../constants";
import {
  customToast,
  isArrayWithItems,
  numberToDecimals,
  calculatePercentage,
} from "../../../../shared";
import IconButton from "../../../../shared/components/IconButton/IconButton";
import CustomTooltip from "../../../../shared/components/ToolTip/CustomTooltip";
import { eventBookingActions } from "../../../../redux/actions";

const CheckoutRow = ({
  label,
  value,
  isStrong = false,
  className = EMPTY_STRING,
}) => {
  return (
    <div
      className={`d-flex justify-content-between align-items-center ${className}`}
    >
      <p className="label-content-600 mt-3 mb-3">{label}</p>
      <p
        className={`${
          isStrong ? "label-content-600" : "text-content"
        } mt-3 mb-3 checkout-amount`}
      >
        {value}
      </p>
    </div>
  );
};

const Checkout = (props) => {
  const {
    updatedFormState,
    totalFee,
    totalTip,
    serviceFee,
    taxPercentValue,
    checkboxes,
    handleCheckboxChange,
    setIsModalOpen,
    promoCodeDiscount,
    setPromoCodeDiscount,
    isGiftCardChecked,
    setIsGiftCardChecked,
    giftCardDiscount,
    setGiftCardDiscount,
    discount,
    remainingBalance,
    alreadyPaidAmount,
    showDiscountButton,
    totalFeeBackupRef,
    initialTotalFeeRef,
    giftCardDiscountApplied,
    updatedTotal,
    resetGiftCard,
    editEventOrderDetails,
    getTaxForLocation,
    setIsAddressSubmitted,
    isAddressSubmitted,
    billingDetails,
    setBillingDetails,
    isAddUpdateRecap,
    isExtendedCase,
    isTipCase,
  } = props;

  const { t } = useTranslation("common");

  const [errors, setErrors] = useState({
    termsAndConditions: EMPTY_STRING,
    privacyPolicy: EMPTY_STRING,
  });

  // Total Amount before applying gift card
  const totalAmount = useMemo(
    () => totalFee + serviceFee + taxPercentValue + totalTip,
    [totalFee, serviceFee, taxPercentValue, totalTip]
  );

  // handle the discount and
  const handlePromoCodeSubmit = (e, status) => {
    e.preventDefault();
    if (props.isAuthenticated) {
      const formData = new FormData(e.target);
      const formProps = Object.fromEntries(formData);
      const dateToSend = {
        code:
          status === discountStatus.PROMO_CODE
            ? formProps.promoCode
            : formProps.giftCard,
        userId: props.userId,
        codeType: status,
      };

      props.applyDiscount(dateToSend, onApplyDiscountSuccess);
    }
  };

  const onApplyDiscountSuccess = (data, codeType) => {
    if (codeType === discountStatus.GIFT_CARD) {
      setGiftCardDiscount(data || null);
    } else if (codeType === discountStatus.PROMO_CODE) {
      resetPromoCodeDiscount(data);
      resetGiftCard();
    }
  };

  const resetPromoCodeDiscount = (data) => {
    setPromoCodeDiscount(data || null);
    initialTotalFeeRef.current = totalFeeBackupRef.current - alreadyPaidAmount;
  };

  const handleCheckbox = (key) => {
    handleCheckboxChange(key);

    const updated = { ...checkboxes, [key]: !checkboxes[key] };
    if (updated[key]) {
      setErrors((prev) => ({ ...prev, [key]: EMPTY_STRING }));
    }
  };

  const validateCheckboxes = () => {
    let newErrors = {
      termsAndConditions: EMPTY_STRING,
      privacyPolicy: EMPTY_STRING,
    };
    let isValid = true;

    if (!checkboxes.termsAndConditions) {
      newErrors.termsAndConditions = messages.ACCEPT_TERMS_AND_CONDITION;
      isValid = false;
    }

    if (!checkboxes.privacyPolicy) {
      newErrors.privacyPolicy = messages.ACCEPT_PRIVACY_POLICY;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(null);
  const [isGiftCardCodeValid, setIsGiftCardCodeValid] = useState(null);

  const handleCheckoutSubmit = (e) => {
    e.preventDefault();
    if (!validateCheckboxes()) return;
    setIsModalOpen(true);
    props.handleCheckout();
  };

  const handleGiftCardSubmit = (e) => {
    e.preventDefault();

    const input = e.target.giftCard;

    if (!input.value.trim()) {
      setIsGiftCardCodeValid(messages.REQUIRED_FIELD);
      return;
    }

    setIsModalOpen(true);
    handlePromoCodeSubmit(e, discountStatus.GIFT_CARD);
  };

  const handleEditAddress = () => {
    setIsAddressSubmitted(false);
    resetPromoCodeDiscount(null);
    resetGiftCard();
  };

  const handleBillingAddressSelect = async (value) => {
    try {
      const results = await geocodeByAddress(value);
      const { lat, lng } = await getLatLng(results?.[0]);

      const components = results?.[0]?.address_components || [];
      const componentMap = components.reduce((acc, comp) => {
        comp.types.forEach((type) => {
          acc[type] = {
            long: comp.long_name,
            short: comp.short_name,
          };
        });
        return acc;
      }, {});

      setBillingDetails({
        ...billingDetails,
        address: value,
        city: componentMap.locality?.long || EMPTY_STRING,
        country: componentMap.country?.long || EMPTY_STRING,
        countryCode: componentMap.country?.short || EMPTY_STRING,
        stateOrProvince:
          componentMap.administrative_area_level_1?.short || EMPTY_STRING,
        postalCode: componentMap.postal_code?.long || EMPTY_STRING,
        longitude: lng,
        latitude: lat,
      });
    } catch (error) {
      console.error(error);

      setBillingDetails({
        ...billingDetails,
        address: value,
      });
    }
  };

  const shouldDisableBillingFields =
    props.isEditEvent || props.isSBTipExtendCart;

  const handleBillingSubmit = (event, errors) => {
    if (errors?.length > 0) return;

    if (!billingDetails) {
      customToast(t("please_enter_billing_details"), toast.TYPE.ERROR);
      return;
    }

    const { countryCode, stateOrProvince, city, postalCode } = billingDetails;

    const amount = numberToDecimals(
      totalFee +
        (serviceFee -
          calculatePercentage(totalTip, props.miscAmounts?.serviceFee))
    );
    const dataToSend = {
      city,
      postalCode,
      countryCode,
      stateOrProvince,
      amount: amount,
    };

    if (!shouldDisableBillingFields) {
      getTaxForLocation(dataToSend, () => {
        setIsAddressSubmitted(true);
        resetPromoCodeDiscount(null);
        resetGiftCard();
      });
    } else {
      setIsAddressSubmitted(true);
    }
  };

  const handleBillingDetails = (e) => {
    const { name, value } = e.target;

    setBillingDetails({
      ...billingDetails,
      [name]: value,
    });
  };

  const shouldShowGiftCardSection = useMemo(() => {
    return (
      (!(!props.isSBTipExtendCart || isExtendedCase) || showDiscountButton) &&
      updatedTotal >= 0 &&
      isGiftCardChecked &&
      totalAmount > 0 &&
      !isTipCase
    );
  }, [
    props.isSBTipExtendCart,
    showDiscountButton,
    updatedTotal,
    isGiftCardChecked,
    totalAmount,
  ]);

  useEffect(() => {
    if (!shouldDisableBillingFields && props.userAddress) {
      handleBillingAddressSelect(props.userAddress);
    }
  }, [shouldDisableBillingFields]);

  return (
    <>
      {!billingDetails || !isAddressSubmitted ? (
        <div className="checkout-container pl-lg-4 pl-0 mt-lg-5 mt-0 pt-2">
          <h2 className="cart-summary-title">{t("billing_details")}</h2>
          <AvForm
            id="billing-address-form"
            onSubmit={handleBillingSubmit}
            autoComplete="off"
            noValidate
          >
            <div className="row">
              <div className="col-12 mb-3 px-0">
                <label className="label-content-600">{t("address")}</label>
                <div className="places-autocomplete-container">
                  <PlacesAutocomplete
                    value={billingDetails?.address || EMPTY_STRING}
                    onChange={(value) =>
                      setBillingDetails({ ...billingDetails, address: value })
                    }
                    onSelect={handleBillingAddressSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <div className="icon-field-con">
                        <div className="position-relative">
                          <AvField
                            name="address"
                            type="text"
                            className="field-with-icon"
                            value={billingDetails?.address || EMPTY_STRING}
                            validate={{
                              required: {
                                value: true,
                                errorMessage: t("enter_a_location"),
                              },
                            }}
                            errorMessage={t("enter_a_location")}
                            placeholder={t("enter_a_location")}
                            {...getInputProps()}
                            disabled={shouldDisableBillingFields}
                          />
                          <div className="input-group-icon">
                            <MapMarkerSvg />
                          </div>
                        </div>

                        {/* Suggestions Dropdown */}
                        <div className="address-suggestions-con px-2 suggestion-zIndex">
                          {loading && <div>{t("loading")}</div>}
                          {suggestions.map((suggestion, index) => {
                            const style = {
                              backgroundColor: suggestion.active
                                ? "#eaeaea"
                                : "#fff",
                            };

                            return (
                              <div key={index}>
                                <div
                                  className="address-suggestions"
                                  {...getSuggestionItemProps(suggestion, {
                                    style,
                                  })}
                                >
                                  <MapMarker className="map-icon" />
                                  {suggestion.description}
                                </div>
                                <hr className="p-0 m-0" />
                              </div>
                            );
                          })}

                          {suggestions.length > 0 && (
                            <div className="google-trademark p-0 m-0">
                              powered by
                              <div className="google-svg-con">
                                <GoogleIcon />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>
              </div>
              <div className="col-12 px-0">
                <label className="label-content-600">city:</label>
                <AvField
                  name="city"
                  type="text"
                  value={billingDetails?.city || EMPTY_STRING}
                  onChange={handleBillingDetails}
                  maxLength={70}
                  pattern="^[a-z,A-Z].{1,70}$"
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("city_is_required"),
                    },
                  }}
                  disabled={shouldDisableBillingFields}
                />
              </div>
              <div className="col-12 px-0">
                <label className="label-content-600">state/province:</label>
                <AvField
                  name="province"
                  type="text"
                  value={billingDetails?.stateOrProvince || EMPTY_STRING}
                  onChange={handleBillingDetails}
                  maxLength={90}
                  pattern="^[a-z,A-Z].{1,90}$"
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("state_province_is_required"),
                    },
                  }}
                  disabled={shouldDisableBillingFields}
                />
              </div>

              <div className="col-12 px-0">
                <label className="label-content-600">postal code:</label>
                <AvField
                  name="postalCode"
                  type="text"
                  value={billingDetails?.postalCode || EMPTY_STRING}
                  onChange={handleBillingDetails}
                  maxLength={20}
                  pattern="^[a-z,A-Z,0-9].{1,20}$"
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("postal_code_is_required"),
                    },
                  }}
                  disabled={shouldDisableBillingFields}
                />
              </div>

              <div className="col-12 px-0">
                <label className="label-content-600">country:</label>
                <AvField
                  name="country"
                  type="text"
                  value={billingDetails?.country || EMPTY_STRING}
                  maxLength={100}
                  pattern="^[a-z,A-Z].{1,100}$"
                  validate={{
                    required: {
                      value: true,
                      errorMessage: t("country_is_required"),
                    },
                  }}
                  disabled
                />
              </div>

              {/* Submit Button */}
              <div className="d-flex justify-content-start mt-1 pt-1">
                <button
                  type="submit"
                  className="button-common button-fill px-4"
                >
                  {t("next")}
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      ) : (
        <div className="checkout-container pl-lg-4 pl-0 mt-lg-5 mt-0 pt-2">
          <div className="checkout-row mb-4">
            <div className="d-flex justify-content-between align-items-start">
              <h2 className="cart-summary-title">{t("billing_details")}</h2>
              <EditSvg
                style={{ cursor: "pointer" }}
                onClick={handleEditAddress}
              />
            </div>
          </div>

          {/* Promo Code */}
          {(!(!props.isSBTipExtendCart || isExtendedCase) ||
            showDiscountButton) &&
            totalFee >= 0 &&
            !isTipCase && (
              <>
                <h2 className="cart-summary-title">{t("apply_coupon_code")}</h2>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();

                    const input = e.target.promoCode;

                    if (!input.value.trim()) {
                      setIsPromoCodeValid("This field is required");
                      return;
                    }

                    setIsModalOpen(true);
                    handlePromoCodeSubmit(e, discountStatus.PROMO_CODE);
                  }}
                  autoComplete="off"
                >
                  <div
                    className={`d-flex align-items-center ${
                      isPromoCodeValid ? "mb-1" : "mb-3"
                    }`}
                  >
                    <input
                      id="form1"
                      name="promoCode"
                      type="text"
                      maxLength={35}
                      className={`form-control coupon-code text-content ${
                        isPromoCodeValid ? "is-invalid" : EMPTY_STRING
                      }`}
                      placeholder="enter promo code"
                      onInput={() => setIsPromoCodeValid(null)}
                    />
                    <button className="button-common button-fill checkout-apply-btn">
                      {messages.APPLY}
                    </button>
                  </div>
                  {isPromoCodeValid && (
                    <p className="invalid-field-error m-0">
                      {isPromoCodeValid}
                    </p>
                  )}
                </form>
              </>
            )}

          {/* Gift Card */}
          {
            //(!props.isSBTipExtendCart || isExtendedCase) &&
            !isTipCase && (
              <div
                className={`d-flex align-items-center pt-1 ${totalAmount < 1 ? "gift-cards-disabled" : "gift-cards"}`}
              >
                <input
                  type="checkbox"
                  name="giftCard"
                  checked={isGiftCardChecked}
                  disabled={totalAmount < 1}
                  onChange={(e) => {
                    setIsGiftCardChecked(e.target.checked);
                    if (!e.target.checked) {
                      setGiftCardDiscount(null);
                    }
                  }}
                  className="custom-checkout gift-card-checkbox"
                />
                <p className="label-content fw-500 mb-0 ml-2 gift-card-checkbox-text">
                  {messages.PAY_WITH_SHUTTERB_CREDITS}
                </p>
              </div>
            )
          }

          {/* Gift Card Form */}
          {shouldShowGiftCardSection && (
            <form onSubmit={handleGiftCardSubmit} autoComplete="off">
              <div className="d-flex align-items-center mt-3 mb-1">
                <input
                  id="form2"
                  name="giftCard"
                  type="text"
                  maxLength={35}
                  className={`form-control coupon-code text-content ${
                    isGiftCardCodeValid ? "is-invalid" : EMPTY_STRING
                  }`}
                  placeholder="enter shutterb credits code"
                  onInput={() => setIsGiftCardCodeValid(null)}
                />
                <button className="button-common button-fill checkout-apply-btn">
                  {messages.APPLY}
                </button>
              </div>
              {isGiftCardCodeValid && (
                <p className="invalid-field-error m-0">{isGiftCardCodeValid}</p>
              )}
            </form>
          )}

          <div>
            {discount > 0 && (
              <CheckoutRow
                label={messages.DISCOUNT}
                value={`$${numberToDecimals(discount)}`}
                className="checkout-row"
              />
            )}

            {promoCodeDiscount?.isGiftCard && (
              <CheckoutRow
                label={messages.REMAINING_BALANCE}
                value={`$${numberToDecimals(remainingBalance)}`}
                className="checkout-row"
              />
            )}

            <CheckoutRow
              label={messages.FEE}
              value={`$${numberToDecimals(totalFee + totalTip)}`}
              className={`checkout-row ${
                totalAmount < 0 ? "balance-due" : EMPTY_STRING
              }`}
            />

            <CheckoutRow
              label={`${messages.SERVICE_FEE}${
                props.miscAmounts?.serviceFee != null
                  ? ` (${numberToDecimals(props.miscAmounts.serviceFee)}%)`
                  : EMPTY_STRING
              }`}
              value={`$${numberToDecimals(serviceFee)}`}
              className={`checkout-row ${
                totalAmount < 0 ? "balance-due" : EMPTY_STRING
              }`}
            />

            <CheckoutRow
              label={messages.SUB_TOTAL}
              value={`$${numberToDecimals(totalFee + serviceFee + totalTip)}`}
              className={`checkout-row ${
                totalAmount < 0 ? "balance-due" : EMPTY_STRING
              }`}
            />

            <CheckoutRow
              label={`${messages.TAX}${
                props.miscAmounts?.tax != null
                  ? ` (${numberToDecimals(props.miscAmounts.tax)}%)`
                  : EMPTY_STRING
              }`}
              value={`$${numberToDecimals(taxPercentValue)}`}
              className={`checkout-row ${
                totalAmount < 0 ? "balance-due" : EMPTY_STRING
              }`}
            />

            <CheckoutRow
              label={messages.TOTAL}
              value={`$${numberToDecimals(totalAmount)}`}
              isStrong
              className={`checkout-row ${
                totalAmount < 0 ? "balance-due" : EMPTY_STRING
              }`}
            />

            {shouldShowGiftCardSection && giftCardDiscount && (
              <div className="gift-card-discount-summary">
                <div
                  className={`d-flex justify-content-between align-items-center already-paid mb-2`}
                >
                  <p className="label-content-600 mb-0">
                    {messages.APPLIED_SHUTTERB_CREDITS}
                  </p>
                  <p className="label-content-600 mb-0">{`$${numberToDecimals(giftCardDiscountApplied)}`}</p>
                </div>

                <div
                  className={`d-flex justify-content-between align-items-center update-total`}
                >
                  <p className="label-content-600 mb-0">
                    {messages.SHUTTERB_CREDITS_BALANCE}
                  </p>
                  <p className="label-content-600 mb-0">
                    {`$${numberToDecimals(remainingBalance)}`}
                  </p>
                </div>
              </div>
            )}
          </div>
          <form onSubmit={handleCheckoutSubmit} autoComplete="off">
            <div>
              <div className="d-flex align-items-start checkout-terms">
                <input
                  type="checkbox"
                  name="termsAndConditions"
                  checked={checkboxes.termsAndConditions}
                  onChange={() => handleCheckbox("termsAndConditions")}
                  className="custom-checkout checkout-checkbox"
                />
                <p className="text-content mb-0 ml-2">
                  you have read &amp; accepted{" "}
                  <a
                    href={`${process.env.REACT_APP_MARKETING_SITE_BASE_URL}/terms-and-conditions/`}
                    target="_blank"
                    without
                    rel="noopener noreferrer"
                  >
                    {t("terms_and_condition")}
                  </a>
                </p>
              </div>

              {errors.termsAndConditions && (
                <p className="invalid-field-error mb-0 mt-1">
                  {errors.termsAndConditions}
                </p>
              )}

              <div className="d-flex align-items-start checkout-terms">
                <input
                  type="checkbox"
                  name="privacyPolicy"
                  checked={checkboxes.privacyPolicy}
                  onChange={() => handleCheckbox("privacyPolicy")}
                  className="custom-checkout checkout-checkbox"
                />
                <p className="text-content mb-0 ml-2">
                  you have read &amp; accepted our{" "}
                  <a
                    href={`${process.env.REACT_APP_MARKETING_SITE_BASE_URL}/privacy-policy/`}
                    target="_blank"
                    without
                    rel="noopener noreferrer"
                  >
                    {t("privacy_policy")}
                  </a>
                </p>
              </div>

              {errors.privacyPolicy && (
                <p className="invalid-field-error mb-0 mt-1">
                  {errors.privacyPolicy}
                </p>
              )}
              {!isTipCase && (
                <>
                  <div className="d-flex align-items-start checkout-terms">
                    <input
                      type="checkbox"
                      name="isConsentForMarketing"
                      checked={checkboxes.isConsentForMarketing}
                      onChange={() =>
                        handleCheckboxChange("isConsentForMarketing")
                      }
                      className="custom-checkout checkout-checkbox"
                    />
                    <p className="text-content mb-0 ml-2">
                      you grant shutterb permission to feature your content on
                      our social channels, website, and in marketing materials.{" "}
                      <CustomTooltip
                        id={`tooltip1`}
                        title={t("shutterb_permission")}
                        arrow
                        placement="top"
                      >
                        <span tabIndex="0">
                          <IconButton
                            icon={<InfoIcon />}
                            width="12px"
                            height="12px"
                          />
                        </span>
                      </CustomTooltip>
                    </p>
                  </div>

                  <div className="d-flex align-items-start checkout-terms">
                    <input
                      type="checkbox"
                      name="isGallerySharedPermission"
                      checked={checkboxes.isGallerySharedPermission}
                      onChange={() =>
                        handleCheckboxChange("isGallerySharedPermission")
                      }
                      className="custom-checkout checkout-checkbox"
                    />
                    <p className="text-content mb-0 ml-2">
                      shutterb can give access to the event gallery directly to
                      my guests so that they can enjoy and share the content as
                      well!
                    </p>
                  </div>
                </>
              )}
            </div>

            {!isGiftCardChecked && (
              <button
                type="submit"
                disabled={
                  props.loadingButton || !isArrayWithItems(updatedFormState)
                }
                className="button-common button-fill checkout-btn mt-3"
              >
                {totalAmount <= 0
                  ? messages.CONFIRM_BOOKING
                  : `${messages.PROCEED_TO_CHECKOUT} ($${numberToDecimals(
                      totalAmount
                    )})`}
              </button>
            )}
          </form>

          {shouldShowGiftCardSection && (
            <button
              type="button"
              disabled={
                props.loadingButton || !isArrayWithItems(updatedFormState)
              }
              className="button-common button-fill checkout-btn mt-3"
              onClick={handleCheckoutSubmit}
            >
              {messages.PROCEED_TO_CHECKOUT}
              {updatedTotal > 0
                ? ` ($${numberToDecimals(updatedTotal)})`
                : EMPTY_STRING}
            </button>
          )}
        </div>
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userId: state.authentication.userId,
  userAddress: state.authentication.userAddress,
  billingDetails: state.eventBooking.billingDetails,
  loadingButton: state.system.loadingButton,
  isEditEvent: state.eventBooking.isEditEvent,
  isAuthenticated: state.authentication.isAuthenticated,
  isSBTipExtendCart: state.eventBooking.isSBTipExtendCart,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setBillingDetails: (data) =>
    dispatch(eventBookingActions.setBillingDetails(data)),
  applyDiscount: (dateToSend, onResponseCallback) =>
    dispatch(eventBookingActions.applyDiscount(dateToSend, onResponseCallback)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
